import { CloudOff, RefreshCw, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenHeader } from '../components/layout/ScreenHeader';
import { useTransitionNavigate } from '../hooks/useTransitionNavigate';
import { getApiErrorMessage } from '../lib/api-error';
import { useGetTrainerProfileQuery } from '../store/coachApi';
import { useSync } from '../store/sync';

const SettingsAdvancedPage = () => {
  const { t } = useTranslation();
  const navigate = useTransitionNavigate();
  const {
    isOnline,
    pendingCount,
    lastSyncAt,
    isSyncing,
    lastSyncError,
    syncNow,
    clearLocalData,
  } = useSync();
  const { refetch } = useGetTrainerProfileQuery();
  const [syncMessage, setSyncMessage] = useState('');
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  const handleSync = async () => {
    setSyncMessage('');
    try {
      await syncNow();
      setSyncMessage(t('Синхронизация завершена'));
    } catch (err) {
      setSyncMessage(getApiErrorMessage(err, 'Не удалось синхронизировать'));
    }
  };

  const handleClear = async () => {
    setSyncMessage('');
    try {
      await clearLocalData();
      setShowClearConfirm(false);
      setSyncMessage(t('Локальные данные очищены'));
      void refetch();
    } catch (err) {
      setSyncMessage(getApiErrorMessage(err, 'Не удалось очистить данные'));
    }
  };

  const formatLastSync = (iso: string | null) => {
    if (!iso) return t('Никогда');
    return new Date(iso).toLocaleString();
  };

  return (
    <div className="page-container space-y-8">
      <ScreenHeader
        title={t('Офлайн-данные')}
        onBack={() => navigate('/settings')}
        backLabel={t('Назад')}
      />

      <div className="card max-w-xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-slate-600">
            <CloudOff size={20} />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">{t('Офлайн-данные')}</h2>
            <p className="text-sm text-slate-500">{t('Локальный кэш и очередь синхронизации')}</p>
          </div>
        </div>

        <dl className="space-y-3 text-sm mb-6">
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">{t('Статус сети')}</dt>
            <dd className="font-semibold text-slate-800">{isOnline ? t('Онлайн') : t('Офлайн')}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">{t('Ожидают синхронизации')}</dt>
            <dd className="font-semibold text-slate-800">{pendingCount}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="text-slate-500">{t('Последняя синхронизация')}</dt>
            <dd className="font-semibold text-slate-800 text-right">{formatLastSync(lastSyncAt)}</dd>
          </div>
        </dl>

        {lastSyncError && (
          <div className="p-3 mb-4 bg-amber-500/10 border border-amber-500/20 text-amber-800 rounded-2xl text-sm">
            {lastSyncError}
          </div>
        )}

        {syncMessage && (
          <div className="p-3 mb-4 bg-emerald-500/10 border border-emerald-500/20 text-emerald-700 rounded-2xl text-sm">
            {syncMessage}
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            type="button"
            className="btn-primary flex items-center justify-center gap-2"
            onClick={handleSync}
            disabled={!isOnline || isSyncing || pendingCount === 0}
          >
            <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
            {isSyncing ? t('Синхронизация...') : t('Синхронизировать сейчас')}
          </button>
          <button
            type="button"
            className="btn-secondary flex items-center justify-center gap-2 text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => setShowClearConfirm(true)}
          >
            <Trash2 size={16} />
            {t('Очистить локальные данные')}
          </button>
        </div>

        {showClearConfirm && (
          <div className="mt-4 p-4 rounded-2xl border border-red-200 bg-red-50/50 space-y-3">
            <p className="text-sm text-red-800">
              {t('Будут удалены все локально сохранённые данные и очередь синхронизации. Данные на сервере не изменятся.')}
            </p>
            <div className="flex gap-3">
              <button type="button" className="btn-primary bg-red-600 hover:bg-red-700" onClick={handleClear}>
                {t('Подтвердить очистку')}
              </button>
              <button type="button" className="btn-secondary" onClick={() => setShowClearConfirm(false)}>
                {t('Отмена')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsAdvancedPage;
